import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import {
  Activity, Globe, FileBarChart, Users, MapPin, Loader2, Download,
  AlertTriangle, RefreshCw, Newspaper, ExternalLink, Clock, Search, CheckCircle, XCircle,
} from 'lucide-react';

/**
 * Founder Cockpit (Phase 1) — native, privacy-preserving analytics.
 *
 * 6 panels:
 *   1. Live pulse (5m/1h/24h/7d windows; auto-refresh every 30s)
 *   2. Geo map (country totals + top cities; needs MaxMind .mmdb file)
 *   3. Top pages (last 7d)
 *   4. Referrer pie (last 30d)
 *   5. Funnel cards (visit → signup → publish → leads → bookings)
 *   6. Recent activity feed (logins + bookings)
 *
 * Every panel write to admin_audit_log (server-side). 30s polling on pulse.
 */

const PULSE_REFRESH_MS = 30_000;

export default function FounderCockpitTab() {
  const [pulse, setPulse] = useState(null);
  const [pulseAt, setPulseAt] = useState(null);
  const [geo, setGeo] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [activity, setActivity] = useState(null);
  const [seoPush, setSeoPush] = useState(null);
  const [heroAb, setHeroAb] = useState(null);
  const [voiceStuck, setVoiceStuck] = useState(null);
  const [error, setError] = useState('');

  const loadPulse = useCallback(async () => {
    try {
      const r = await api.get('/admin/analytics/pulse');
      setPulse(r.data);
      setPulseAt(new Date());
    } catch (e) { /* silent — keep last good */ }
  }, []);

  const loadEverything = useCallback(async () => {
    try {
      const [g, t, ref, f, a, sp, ab, vs] = await Promise.all([
        api.get('/admin/analytics/geo?days=30'),
        api.get('/admin/analytics/top-pages?days=7'),
        api.get('/admin/analytics/referrers?days=30'),
        api.get('/admin/analytics/funnel?days=30'),
        api.get('/admin/analytics/activity?limit=25'),
        api.get('/admin/analytics/seo-push?days=7'),
        api.get('/admin/analytics/hero-ab?days=30'),
        api.get('/admin/analytics/voice-stuck'),
      ]);
      setGeo(g.data); setTopPages(t.data); setReferrers(ref.data);
      setFunnel(f.data); setActivity(a.data); setSeoPush(sp.data);
      setHeroAb(ab.data); setVoiceStuck(vs.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load analytics');
    }
  }, []);

  useEffect(() => {
    loadPulse(); loadEverything();
    const id = setInterval(loadPulse, PULSE_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadPulse, loadEverything]);

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 flex items-start gap-3" data-testid="cockpit-error">
        <AlertTriangle size={18} className="text-rose-600 mt-0.5" />
        <div className="text-sm text-rose-800"><strong>Founder Cockpit failed to load:</strong> {error}</div>
      </div>
    );
  }

  const exportCsv = (panel) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export/${panel}?days=30`;
    const tok = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${panel}-30d.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => {
        // Non-fatal — surface in console; user can retry. Avoid crashing the page.
        // eslint-disable-next-line no-console
        console.warn('CSV export failed:', err);
        alert(`CSV export failed: ${err.message || 'unknown error'}`);
      });
  };

  return (
    <div className="space-y-8" data-testid="founder-cockpit">
      {/* --- Live Pulse --- */}
      <PulsePanel pulse={pulse} pulseAt={pulseAt} onRefresh={loadPulse} />

      {/* --- SEO Engine: Lever 1 health (Indexing API + IndexNow) --- */}
      <SeoPushPanel seoPush={seoPush} />

      {/* --- Voice-Stuck Doctors: Lever 1 leak detector --- */}
      <VoiceStuckPanel voiceStuck={voiceStuck} reload={loadEverything} />

      {/* --- Hero A/B Test (Live vs Mock-Male vs Mock-Female) --- */}
      <HeroAbPanel heroAb={heroAb} />

      {/* --- Funnel + Referrers --- */}
      <div className="grid lg:grid-cols-2 gap-6">
        <FunnelPanel funnel={funnel} />
        <ReferrerPanel referrers={referrers} onExport={() => exportCsv('referrers')} />
      </div>

      {/* --- Top Pages + Geo --- */}
      <div className="grid lg:grid-cols-2 gap-6">
        <TopPagesPanel topPages={topPages} onExport={() => exportCsv('top-pages')} />
        <GeoPanel geo={geo} onExport={() => exportCsv('geo')} />
      </div>

      {/* --- Activity Feed --- */}
      <ActivityPanel activity={activity} onExport={() => exportCsv('activity')} />
    </div>
  );
}

// ===================== PULSE =====================
function PulsePanel({ pulse, pulseAt, onRefresh }) {
  if (!pulse) return <PanelSkeleton title="Live Pulse" />;
  const W = pulse.windows || {};
  const tile = (label, val) => (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
      <p className="text-sm sm:text-base uppercase tracking-wider text-slate-500 font-bold">{label}</p>
      <p className="text-5xl sm:text-6xl lg:text-7xl font-medium text-slate-900 mt-3 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{val.toLocaleString()}</p>
    </div>
  );
  return (
    <section data-testid="cockpit-pulse">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Activity size={26} className="text-emerald-600" /> Live Pulse
          </h2>
          <p className="text-sm text-slate-500 mt-2">
            Auto-refreshes every 30s · Last updated {pulseAt ? new Date(pulseAt).toLocaleTimeString() : '—'}
            {pulse.geoip_loaded === false && (
              <span className="ml-3 inline-flex items-center gap-1 text-amber-600">
                <AlertTriangle size={13} /> GeoIP DB not loaded
              </span>
            )}
          </p>
        </div>
        <button onClick={onRefresh}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-sky-600 px-4 py-2 rounded-lg border border-slate-200 hover:border-sky-300"
          data-testid="pulse-refresh-btn">
          <RefreshCw size={14} /> Refresh now
        </button>
      </div>

      {['5m', '1h', '24h', '7d'].map((win) => (
        <div key={win} className="mb-5">
          <p className="text-base uppercase tracking-wider text-slate-500 font-bold mb-3">Last {win}</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {tile('Page views', W[win]?.page_views ?? 0)}
            {tile('Clicks', W[win]?.clicks ?? 0)}
            {tile('Bookings', W[win]?.bookings ?? 0)}
            {tile('Doctor signups', W[win]?.signups_doctor ?? 0)}
            {tile('Patient signups', W[win]?.signups_patient ?? 0)}
          </div>
        </div>
      ))}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-6">
          <p className="text-sm uppercase tracking-wider text-sky-700 font-bold">Active Doctors</p>
          <p className="text-4xl sm:text-5xl font-medium text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{pulse.totals?.active_doctors ?? 0}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <p className="text-sm uppercase tracking-wider text-amber-700 font-bold">Pending Payments</p>
          <p className="text-4xl sm:text-5xl font-medium text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{pulse.totals?.pending_payments ?? 0}</p>
        </div>
      </div>
    </section>
  );
}

// ===================== SEO ENGINE (Lever 1) =====================
function SeoPushPanel({ seoPush }) {
  const [retrigger, setRetrigger] = useState({ status: 'idle', count: 0, ts: null, err: '' });

  /** Manual IndexNow re-ping — fires the bulk re-trigger of every platform URL
   *  + all active doctor profiles. Useful right after a deploy that changes
   *  many pages at once (new feature launches, copy edits, etc.). */
  const fireRetrigger = async () => {
    if (retrigger.status === 'pending') return;
    setRetrigger({ status: 'pending', count: 0, ts: null, err: '' });
    try {
      const r = await api.post('/platform-seo/indexnow-retrigger');
      setRetrigger({ status: 'done', count: r.data?.submitted || 0, ts: new Date(), err: '' });
    } catch (e) {
      setRetrigger({ status: 'error', count: 0, ts: new Date(), err: e.response?.data?.detail || 'Re-trigger failed' });
    }
  };

  if (!seoPush) return <PanelSkeleton title="SEO Engine" />;
  const Tile = ({ label, value, sub, ok }) => (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-2">
        {ok === true && <CheckCircle size={16} className="text-emerald-600" />}
        {ok === false && <XCircle size={16} className="text-rose-600" />}
        <p className="text-sm uppercase tracking-wider text-slate-500 font-bold">{label}</p>
      </div>
      <p className="text-4xl sm:text-5xl font-medium text-slate-900 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
      {sub && <p className="text-sm text-slate-500 mt-2">{sub}</p>}
    </div>
  );
  const total = seoPush.total_pushes;
  const gReady = seoPush.google_indexing_configured;
  return (
    <section data-testid="cockpit-seo-push">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Search size={26} className="text-violet-600" /> SEO Engine — last {seoPush.days}d
          </h2>
          <p className="text-sm text-slate-500 mt-2">
            Lever 1 of the 5-lever SEO promise: every new doctor profile and article is auto-pushed to Bing IndexNow + Google Indexing API within seconds of publish.
            {!gReady && (
              <span className="ml-2 inline-flex items-center gap-1.5 text-amber-700 font-medium">
                <AlertTriangle size={13} /> Google Indexing API not yet connected — drop the service-account JSON at <code className="bg-amber-50 px-1 rounded text-xs">/app/backend/data/google-indexing-sa.json</code>.
              </span>
            )}
          </p>
        </div>
        {/* Manual re-trigger — single click to re-ping every platform URL + all active doctor profiles. */}
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button onClick={fireRetrigger} disabled={retrigger.status === 'pending'}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-60"
            data-testid="seo-retrigger-btn">
            {retrigger.status === 'pending'
              ? <><Loader2 size={14} className="animate-spin" /> Re-pinging IndexNow…</>
              : <><RefreshCw size={14} /> Re-ping IndexNow now</>}
          </button>
          {retrigger.status === 'done' && (
            <p className="text-xs text-emerald-700 font-medium" data-testid="seo-retrigger-ok">
              Re-pinged {retrigger.count.toLocaleString()} URLs · {retrigger.ts.toLocaleTimeString()}
            </p>
          )}
          {retrigger.status === 'error' && (
            <p className="text-xs text-rose-600 font-medium max-w-[260px] text-right" data-testid="seo-retrigger-err">{retrigger.err}</p>
          )}
          {retrigger.status === 'idle' && (
            <p className="text-[10px] text-slate-400">Hit this after each deploy to fast-track Bing crawl.</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Total pushes" value={total.toLocaleString()} />
        <Tile label="Bing IndexNow" value={`${seoPush.indexnow_success_rate}%`} sub={`${total} / ${total} attempted`} ok={seoPush.indexnow_success_rate >= 80} />
        <Tile label="Google Indexing API" value={`${seoPush.google_success_rate}%`} sub={gReady ? 'connected' : 'not configured'} ok={gReady && seoPush.google_success_rate >= 80} />
        <Tile label="Pages pushed" value={(seoPush.by_kind || []).reduce((a, b) => a + b.count, 0)} sub={(seoPush.by_kind || []).map(k => `${k.kind}: ${k.count}`).join(' · ')} />
      </div>
      {seoPush.recent && seoPush.recent.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-7">
          <p className="text-sm uppercase tracking-wider text-slate-500 font-bold mb-4">Recent pushes</p>
          <ul className="space-y-2.5 text-sm">
            {seoPush.recent.slice(0, 8).map((r, i) => (
              <li key={i} className="flex items-center gap-3 border-b border-slate-100 pb-2 last:border-0">
                {r.indexnow_ok ? <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={14} className="text-rose-400 flex-shrink-0" />}
                <span className="text-slate-700 flex-1 truncate text-xs sm:text-sm">{r.url}</span>
                <span className="text-xs text-slate-400">{r.kind}</span>
                <span className={`text-xs font-medium ${r.google_ok ? 'text-emerald-600' : (r.google_reason === 'creds_missing' ? 'text-amber-600' : 'text-rose-500')}`}>
                  G: {r.google_ok ? 'ok' : (r.google_reason || 'fail')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ===================== FUNNEL =====================
function FunnelPanel({ funnel }) {
  if (!funnel) return <PanelSkeleton title="Funnel" />;
  const max = Math.max(...funnel.steps.map(s => s.count), 1);
  return (
    <section data-testid="cockpit-funnel" className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8">
      <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-3 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <FileBarChart size={24} className="text-violet-600" /> Funnel — last {funnel.days}d
      </h2>
      <div className="space-y-4">
        {funnel.steps.map((s) => (
          <div key={s.key} data-testid={`funnel-step-${s.key}`}>
            <div className="flex items-center justify-between text-base mb-2">
              <span className="text-slate-700 font-medium">{s.label}</span>
              <span className="text-slate-900 font-bold tabular-nums text-xl">{s.count.toLocaleString()}</span>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-sky-500 to-violet-500"
                style={{ width: `${Math.max(2, (s.count / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ===================== REFERRERS =====================
function ReferrerPanel({ referrers, onExport }) {
  if (!referrers) return <PanelSkeleton title="Referrers" />;
  const total = referrers.rows.reduce((a, r) => a + r.count, 0) || 1;
  const palette = ['bg-sky-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-slate-500', 'bg-cyan-500', 'bg-fuchsia-500'];
  return (
    <section data-testid="cockpit-referrers" className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Globe size={24} className="text-sky-600" /> Referrers — last {referrers.days}d
        </h2>
        <button onClick={onExport} className="text-sm text-slate-600 hover:text-sky-600 flex items-center gap-1.5" data-testid="export-referrers-btn">
          <Download size={14} /> CSV
        </button>
      </div>
      <div className="space-y-3.5">
        {referrers.rows.map((r, i) => (
          <div key={r.source} className="flex items-center gap-4" data-testid={`referrer-${r.source}`}>
            <span className={`w-3 h-3 rounded-full ${palette[i % palette.length]}`} />
            <span className="text-base text-slate-700 capitalize flex-1">{r.source.replace(/-/g, ' ')}</span>
            <span className="text-lg text-slate-900 font-bold tabular-nums">{r.count.toLocaleString()}</span>
            <span className="text-sm text-slate-400 w-14 text-right">{((r.count / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ===================== TOP PAGES =====================
function TopPagesPanel({ topPages, onExport }) {
  if (!topPages) return <PanelSkeleton title="Top Pages" />;
  return (
    <section data-testid="cockpit-top-pages" className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Newspaper size={24} className="text-emerald-600" /> Top Pages — last {topPages.days}d
        </h2>
        <button onClick={onExport} className="text-sm text-slate-600 hover:text-sky-600 flex items-center gap-1.5" data-testid="export-top-pages-btn">
          <Download size={14} /> CSV
        </button>
      </div>
      {topPages.rows.length === 0 ? (
        <p className="text-base text-slate-400 italic">No views in the last 7 days.</p>
      ) : (
        <ol className="space-y-3 text-base">
          {topPages.rows.slice(0, 12).map((r, i) => (
            <li key={r.doctor_slug} className="flex items-center gap-3" data-testid={`top-page-${r.doctor_slug}`}>
              <span className="text-sm text-slate-400 font-mono w-7">{i + 1}.</span>
              <a href={`/dr/${r.doctor_slug}`} target="_blank" rel="noreferrer"
                 className="text-slate-700 hover:text-sky-600 flex-1 truncate flex items-center gap-2">
                {r.doctor_name} <ExternalLink size={13} className="opacity-50" />
              </a>
              <span className="text-lg text-slate-900 font-bold tabular-nums">{r.views.toLocaleString()}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ===================== GEO =====================
function GeoPanel({ geo, onExport }) {
  if (!geo) return <PanelSkeleton title="Geo" />;
  const dbMissing = geo.geoip_loaded === false;
  return (
    <section data-testid="cockpit-geo" className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <MapPin size={24} className="text-rose-600" /> Geo — last {geo.days}d
        </h2>
        <button onClick={onExport} className="text-sm text-slate-600 hover:text-sky-600 flex items-center gap-1.5" data-testid="export-geo-btn">
          <Download size={14} /> CSV
        </button>
      </div>
      {dbMissing && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 text-sm text-amber-900" data-testid="geo-db-missing-banner">
          <strong>GeoIP database not loaded.</strong> Drop <code className="bg-amber-100 px-1 rounded">GeoLite2-City.mmdb</code> at <code className="bg-amber-100 px-1 rounded">/app/backend/data/</code> from <a href="https://maxmind.com" target="_blank" rel="noreferrer" className="underline">maxmind.com</a> (free signup) — current events are bucketed as "Unknown" until then.
        </div>
      )}
      {/* Country leaderboard */}
      <p className="text-sm uppercase tracking-wider text-slate-500 font-bold mb-3">Top countries</p>
      {geo.country_totals.length === 0 ? (
        <p className="text-base text-slate-400 italic">No events recorded yet.</p>
      ) : (
        <ul className="space-y-2.5 text-base mb-7">
          {geo.country_totals.slice(0, 8).map((c) => (
            <li key={c.country} className="flex items-center gap-3" data-testid={`geo-country-${c.country.toLowerCase().replace(/\s+/g, '-')}`}>
              <span className="text-slate-700 flex-1">{c.country}</span>
              <span className="text-lg text-slate-900 font-bold tabular-nums">{c.count.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
      {/* Top cities */}
      <p className="text-sm uppercase tracking-wider text-slate-500 font-bold mb-3">Top cities</p>
      {geo.rows.length === 0 ? (
        <p className="text-base text-slate-400 italic">No events recorded yet.</p>
      ) : (
        <ul className="space-y-2.5 text-base">
          {geo.rows.slice(0, 8).map((r, i) => (
            <li key={`${r.country}-${r.state}-${r.city}-${i}`} className="flex items-center gap-3">
              <span className="text-sm text-slate-500 w-6">{i + 1}.</span>
              <span className="text-slate-700 flex-1 truncate">{r.city}, {r.state}, {r.country}</span>
              <span className="text-lg text-slate-900 font-bold tabular-nums">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===================== ACTIVITY =====================
function ActivityPanel({ activity, onExport }) {
  if (!activity) return <PanelSkeleton title="Activity" />;
  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString();
  };
  return (
    <section data-testid="cockpit-activity" className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Users size={24} className="text-cyan-600" /> Recent Activity
        </h2>
        <button onClick={onExport} className="text-sm text-slate-600 hover:text-sky-600 flex items-center gap-1.5" data-testid="export-activity-btn">
          <Download size={14} /> CSV
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-7">
        {/* Logins */}
        <div>
          <p className="text-sm uppercase tracking-wider text-slate-500 font-bold mb-3">Recent successful logins</p>
          {activity.logins.length === 0 ? (
            <p className="text-base text-slate-400 italic">No logins in window.</p>
          ) : (
            <ul className="space-y-3 text-base" data-testid="activity-logins">
              {activity.logins.slice(0, 10).map((l, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm border-b border-slate-100 pb-3">
                  <Clock size={14} className="text-slate-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700 truncate font-medium">{l.email || '—'}</p>
                    <p className="text-slate-500 mt-0.5">
                      {l.city}, {l.country} · {fmt(l.created_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Bookings */}
        <div>
          <p className="text-sm uppercase tracking-wider text-slate-500 font-bold mb-3">Recent bookings</p>
          {activity.bookings.length === 0 ? (
            <p className="text-base text-slate-400 italic">No recent bookings.</p>
          ) : (
            <ul className="space-y-3 text-base" data-testid="activity-bookings">
              {activity.bookings.slice(0, 10).map((b) => (
                <li key={b.id} className="flex items-start gap-2.5 text-sm border-b border-slate-100 pb-3">
                  <Clock size={14} className="text-slate-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700 truncate font-medium">{b.doctor_name}</p>
                    <p className="text-slate-500 mt-0.5">
                      {b.date} {b.start_time} · status: {b.status} · booked {fmt(b.created_at)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// ===================== Hero A/B Test Panel =====================
function HeroAbPanel({ heroAb }) {
  if (!heroAb) return <PanelSkeleton title="Hero A/B test…" />;
  const rows = heroAb.rows || [];
  const totals = heroAb.totals || {};
  const winner = [...rows].sort((a, b) => b.ctr_pct - a.ctr_pct)[0];
  const colors = {
    A: { dot: 'bg-sky-500', text: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200' },
    B: { dot: 'bg-indigo-500', text: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
    C: { dot: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
  };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="hero-ab-panel">
      <div className="flex items-start justify-between mb-5 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-600 mb-1">Hero A/B Test · Live</p>
          <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Which homepage hero converts best?
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Sticky 30-day bucket · last {heroAb.days}d · {totals.impressions || 0} impressions · {totals.cta_clicks || 0} CTA clicks
          </p>
        </div>
        {winner && winner.impressions > 0 && (
          <div className="text-right hidden sm:block">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-600">Leader</p>
            <p className="text-base font-semibold text-slate-900">{winner.label}</p>
            <p className="text-xs text-slate-500">{winner.ctr_pct}% CTR</p>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {rows.map(r => {
          const c = colors[r.variant] || colors.A;
          const isWinner = winner && winner.variant === r.variant && r.impressions > 0;
          return (
            <div key={r.variant} className={`rounded-xl border ${c.border} ${c.bg} p-5`} data-testid={`hero-ab-card-${r.variant}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${c.text}`}>Variant {r.variant}</span>
                {isWinner && (
                  <span className="ml-auto text-[10px] uppercase tracking-[0.16em] font-bold bg-emerald-200 text-emerald-900 px-2 py-0.5 rounded-full">Best</span>
                )}
              </div>
              <p className="text-[15px] font-semibold text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{r.label}</p>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{r.impressions}</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mt-0.5">Imprs</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{r.cta_clicks}</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mt-0.5">Clicks</p>
                </div>
                <div>
                  <p className={`text-2xl font-bold ${c.text}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{r.ctr_pct}%</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mt-0.5">CTR</p>
                </div>
              </div>
              <div className="mt-3 h-1.5 bg-white rounded-full overflow-hidden border border-slate-200">
                <div className={`h-full ${c.dot}`} style={{ width: `${Math.min(100, r.traffic_share_pct)}%` }} />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">{r.traffic_share_pct}% of traffic</p>
            </div>
          );
        })}
      </div>
      {totals.impressions === 0 && (
        <p className="mt-4 text-xs text-slate-500 italic">
          No traffic captured yet — visit the homepage in a fresh incognito window to seed an impression.
        </p>
      )}
    </section>
  );
}

// ===================== Voice-Stuck Doctors =====================
function VoiceStuckPanel({ voiceStuck, reload }) {
  const [emailing, setEmailing] = useState({ status: 'idle', msg: '' });
  if (!voiceStuck) return <PanelSkeleton title="Voice-stuck doctors" />;
  const { stuck_count, healthy_count, total_with_drafts, alert_active, rows } = voiceStuck;

  const sendEmail = async () => {
    if (emailing.status === 'pending') return;
    setEmailing({ status: 'pending', msg: '' });
    try {
      const r = await api.post('/admin/analytics/voice-stuck/email-me');
      setEmailing({
        status: 'done',
        msg: r.data?.sent
          ? `Emailed nudge list for ${r.data.stuck_count} doctors.`
          : 'No stuck doctors right now — nothing to email.',
      });
    } catch (e) {
      setEmailing({ status: 'error', msg: e.response?.data?.detail || 'Email send failed' });
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
  };

  return (
    <section data-testid="cockpit-voice-stuck" className={`rounded-2xl border ${alert_active ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200 bg-white'} p-7 sm:p-8`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <AlertTriangle size={26} className={alert_active ? 'text-rose-600' : 'text-emerald-600'} />
            Voice-stuck doctors
            {alert_active && (
              <span className="text-[10px] font-bold uppercase tracking-widest bg-rose-600 text-white px-2 py-1 rounded-full" data-testid="voice-stuck-alert-badge">
                Alert · {stuck_count}+
              </span>
            )}
          </h2>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-3xl">
            Doctors who generated AI drafts but haven't yet hit the 40% authentic-edit threshold on any section.
            Their profiles stay <strong>noindex on Google</strong> and won't get patient leads until they personalise.
            {alert_active && <span className="block mt-1 text-rose-700 font-semibold">⚠ {stuck_count} doctors are stuck — send a nudge today.</span>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <button onClick={sendEmail} disabled={emailing.status === 'pending' || stuck_count === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="voice-stuck-email-btn">
            {emailing.status === 'pending'
              ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
              : <>📧 Email me the nudge list</>}
          </button>
          {emailing.status === 'done' && (
            <p className="text-xs text-emerald-700 font-medium" data-testid="voice-stuck-email-ok">{emailing.msg}</p>
          )}
          {emailing.status === 'error' && (
            <p className="text-xs text-rose-600 font-medium" data-testid="voice-stuck-email-err">{emailing.msg}</p>
          )}
          <button onClick={reload}
            className="text-xs text-slate-500 hover:text-sky-600 inline-flex items-center gap-1"
            data-testid="voice-stuck-refresh-btn">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Stuck</p>
          <p className="text-4xl font-medium text-rose-600 tabular-nums mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="voice-stuck-count">{stuck_count}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Healthy (≥40%)</p>
          <p className="text-4xl font-medium text-emerald-600 tabular-nums mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{healthy_count}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Total with drafts</p>
          <p className="text-4xl font-medium text-slate-900 tabular-nums mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{total_with_drafts}</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-emerald-700 italic">All doctors with AI drafts are above 40%. Nice.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-bold">Doctor</th>
                <th className="px-4 py-3 font-bold">City</th>
                <th className="px-4 py-3 font-bold text-right">Max Voice%</th>
                <th className="px-4 py-3 font-bold">Last Edit</th>
                <th className="px-4 py-3 font-bold">Email</th>
                <th className="px-4 py-3 font-bold text-right">Profile</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 25).map((r) => (
                <tr key={r.doctor_id} className="border-t border-slate-100" data-testid={`voice-stuck-row-${r.slug}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.city || '—'}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold text-rose-600">{r.max_voice_pct}%</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.last_edit_at)}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-[200px]">{r.email || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <a href={`/dr/${r.slug}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-800 text-xs font-semibold">
                      View <ExternalLink size={11} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 25 && (
            <p className="text-xs text-slate-400 italic px-4 py-3 border-t border-slate-100">
              Showing 25 of {rows.length} · email yourself the full list above.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ===================== Skeleton =====================
function PanelSkeleton({ title }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-7 flex items-center gap-3 text-base text-slate-400">
      <Loader2 size={18} className="animate-spin" /> Loading {title}…
    </section>
  );
}
